import { format } from 'date-fns';
import { useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';

import { useStockRemaining } from '@/hooks/useStockRemaining';
import { useCartStore } from '@/stores/cart-store';
import { type CartItem } from '@/types/cart';

type CartStockValidationResult = {
  getStockAwareLimit: (item: CartItem) => number;
  isLoading: boolean;
  validateCartStock: () => {
    invalidItems: Array<{ item: CartItem; limit: number }>;
    isValid: boolean;
  };
};

export function useSyncCartStock(date: Date | null): CartStockValidationResult {
  const items = useCartStore((state) => state.items);
  const removeItem = useCartStore((state) => state.removeItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const { breadOptionsByMenu, breadStockMap, isBreadMap, isLoading, menuStockMap } =
    useStockRemaining(date);

  const syncKey = useMemo(() => {
    const dateStr = date ? format(date, 'yyyy-MM-dd') : 'no-date';
    const itemsKey = items.map((item) => `${item.id}:${item.quantity}`).join('|');
    return `${dateStr}:${itemsKey}`;
  }, [date, items]);

  const syncedKeyRef = useRef<string | null>(null);

  const getStockAwareLimit = (item: CartItem) => {
    const menuId = Number(item.id.split('-')[0]);
    let limit = item.purchaseLimit;

    const menuRemaining = menuStockMap.get(menuId);
    if (menuRemaining !== undefined) {
      if (isBreadMap.get(menuId)) {
        const hasHalfOption =
          item.selectedOptions?.some((option) => option.option_value.includes('하프')) ?? false;
        const unit = hasHalfOption ? 0.5 : 1;
        limit = Math.min(limit, Math.floor(menuRemaining / unit));
      } else {
        limit = Math.min(limit, Math.floor(menuRemaining));
      }
    }

    const menuBreadOptions = breadOptionsByMenu[String(menuId)] ?? [];
    const selectedBreadOption = item.selectedOptions?.find((option) =>
      menuBreadOptions.some((candidate) => candidate.optionId === option.option_id)
    );
    if (selectedBreadOption) {
      const breadOption = menuBreadOptions.find(
        (candidate) => candidate.optionId === selectedBreadOption.option_id
      );
      if (breadOption) {
        const breadRemaining = breadStockMap.get(breadOption.breadId);
        if (breadRemaining !== undefined) {
          limit = Math.min(limit, Math.floor(breadRemaining));
        }
      }
    }

    return Math.max(0, limit);
  };

  const validateCartStock = () => {
    const invalidItems = items
      .map((item) => ({
        item,
        limit: getStockAwareLimit(item),
      }))
      .filter(({ item, limit }) => limit <= 0 || item.quantity > limit);

    return {
      invalidItems,
      isValid: invalidItems.length === 0,
    };
  };

  useEffect(() => {
    if (!date || isLoading || items.length === 0 || syncedKeyRef.current === syncKey) {
      return;
    }

    syncedKeyRef.current = syncKey;

    items.forEach((item) => {
      const limit = getStockAwareLimit(item);

      if (limit <= 0) {
        removeItem(item.id);
        toast.error(`'${item.name}'의 재고가 부족해 장바구니에서 제거되었습니다.`);
        return;
      }

      if (item.quantity > limit) {
        updateQuantity(item.id, limit);
        toast(`'${item.name}'의 수량이 ${limit}개로 조정되었습니다.`);
      }
    });
  }, [date, getStockAwareLimit, isLoading, items, removeItem, syncKey, updateQuantity]);

  return {
    getStockAwareLimit,
    isLoading,
    validateCartStock,
  };
}
